import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import SingleQuestionView from './SingleQuestionView';
import DashboardTopbar from '../components/DashboardTopbar';
import QuestionBlock, { QuestionOption } from '../components/QuestionBlock/QuestionBlock';
import { isValidOptionText } from '../utils/questionOptions';
import PlansModal from '../components/PlansModal';
import { AccessTimeOutlined, PrintOutlined } from '../icons';
import PageLoader from '../components/PageLoader';
import './QuestionsPage.css';

const isPlanLimitError = (error) =>
  error.response?.status === 403 && error.response.data?.code === 'PLAN_LIMIT_REACHED';

const apiUrl = process.env.REACT_APP_API_URL;

const Quiz = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isSubmittingRef = useRef(false);

  const { mock } = location.state || {};
  const [questions, setQuestions] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [timeLeft, setTimeLeft] = useState(4 * 60 * 60); // 4 horas em segundos
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [markedForReview, setMarkedForReview] = useState({});
  const [showPaywall, setShowPaywall] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const mockId = mock.id;
      setLoading(true);
      try {
        const response = await axios.post(apiUrl + "questions/", {
          mockId
        });
        if (response.data.anwseredMock) {
          alert("Você já respondeu este simulado.");
          navigate('/simulados');
        } else {
          setQuestions(response.data);
        }
      } catch (error) {
        if (isPlanLimitError(error)) {
          setShowPaywall(true);
        } else {
          console.error(error);
          alert("Erro ao carregar as questões. Tente novamente mais tarde.");
        }
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [mock]);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const sendAnwsers = () => {
    // Verifica usando Ref (imutável e síncrono)
    if (isSubmittingRef.current) return;

    let array = ["A", "B", "C", "D", "E"];
    if (questions.length !== Object.entries(selectedOptions).length) {
      alert("Você deve responder todas as questões do simulado antes de enviar.");
      return;
    }

    // Marca como enviando IMEDIATAMENTE (síncrono)
    isSubmittingRef.current = true;
    setSubmitting(true);

    const updatedOptions = Object.entries(selectedOptions).map(([key, option]) => ({
      ...option,
      value: array[option],
      id: questions[key].id
    }));

    let data = {
      "awnsers": updatedOptions,
      "mockId": mock.id
    };

    axios.post(apiUrl + 'sendMock', data)
      .then(response => {
        if (response.data.anwseredMock) {
          alert("Você já realizou esse simulado.");
          navigate(-1);
        } else {
          navigate('/success', { state: { mock: { id: mock.id, name: mock.name } } });
        }
      })
      .catch(error => {
        if (isPlanLimitError(error)) {
          setShowPaywall(true);
        } else {
          console.error(error);
          alert("Erro ao enviar suas respostas. Tente novamente.");
        }
        // Permite nova tentativa em caso de erro
        isSubmittingRef.current = false;
        setSubmitting(false);
      })
      .finally(() => {
        // Na prática, o then() já navega, mas deixamos para segurança
        if (!isSubmittingRef.current) {
          setSubmitting(false);
        }
      });
  };

  const handleOptionChange = (questionIndex, option, optionIndex) => {
    setSelectedOptions({
      ...selectedOptions,
      [questionIndex]: optionIndex
    });
  };

  const toggleEliminated = (questionIndex, optionIndex) => {
    setQuestions(prevQuestions => {
      const newQuestions = [...prevQuestions];
      const q = { ...newQuestions[questionIndex] };
      q.eliminated = q.eliminated || [];
      if (!q.eliminated.includes(optionIndex)) {
        q.eliminated = [...q.eliminated, optionIndex];
      } else {
        q.eliminated = q.eliminated.filter(idx => idx !== optionIndex);
      }
      newQuestions[questionIndex] = q;
      return newQuestions;
    });
  };

  const toggleMarkedForReview = (questionIndex) => {
    setMarkedForReview(prev => ({
      ...prev,
      [questionIndex]: !prev[questionIndex]
    }));
  };

  const getAnswers = (optionIndex) => {
    let array = ["A", "B", "C", "D", "E"]
    return array[optionIndex] + ")"
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getQuestionImageSrc = (imageUrl) => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('data:')) return imageUrl;
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://') || imageUrl.startsWith('//')) return imageUrl;
    return `${apiUrl}uploads/${imageUrl}`;
  };

  const questionsTopbar = (
    <DashboardTopbar
      className="questions-topbar no-print"
      left={(
        <div className="exam-timer">
          <AccessTimeOutlined style={{ fontSize: 16 }} />
          <span className="timer-value">{formatTime(timeLeft)}</span>
        </div>
      )}
      right={(
        <button
          type="button"
          className="print-mock-button"
          onClick={() => window.print()}
        >
          <PrintOutlined style={{ fontSize: 18 }} />
          Imprimir
        </button>
      )}
    />
  );

  if (showPaywall) {
    return (
      <div className="main-container questions-exam-page">
        {questionsTopbar}
        <PlansModal
          trackingPrefix="trial_wall_simulado"
          heroTitle="Você já usou seu simulado gratuito"
          heroSubtitle="Libere acesso completo para continuar praticando com todos os simulados até a prova da Polícia Penal RS."
          onClose={() => navigate('/simulados')}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="main-container questions-exam-page">
        {questionsTopbar}
        <PageLoader message="Carregando as questões..." />
      </div>
    );
  }

  return (
    <div className="main-container questions-exam-page">
      {questionsTopbar}

      <div className="no-print">
        <SingleQuestionView
          mock={mock}
          questions={questions}
          currentQuestionIndex={currentQuestionIndex}
          setCurrentQuestionIndex={setCurrentQuestionIndex}
          selectedOptions={selectedOptions}
          onOptionChange={handleOptionChange}
          markedForReview={markedForReview}
          onToggleReview={toggleMarkedForReview}
          onToggleEliminated={toggleEliminated}
          onSubmit={sendAnwsers}
          submitting={submitting}
          getAnswers={getAnswers}
          getQuestionImageSrc={getQuestionImageSrc}
        />
      </div>

      <div className="print-all-questions">
        {questions.map((question, questionIndex) => (
          <div className="print-question-block" key={question.id}>
            <QuestionBlock
              counterText={`Questão ${questionIndex + 1} de ${questions.length}`}
              subjectName={question.subjectName}
              title={question.title}
              imageSrc={question.image_url ? getQuestionImageSrc(question.image_url) : null}
              imageAlt={`Imagem da questão ${questionIndex + 1}`}
            >
              {question.questions.map((option, optionIndex) => (
                isValidOptionText(option) && (
                  <QuestionOption
                    key={optionIndex}
                    index={optionIndex}
                    letter={getAnswers(optionIndex).replace(')', '')}
                    text={option}
                    status={selectedOptions[questionIndex] === optionIndex ? 'selected' : ''}
                  />
                )
              ))}
            </QuestionBlock>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Quiz;
